import { Alert, Button, Flex } from 'antd'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/router'

type Props = {
  children: ReactNode
  fallbackMessage?: string
}

type State = {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(_error: Error, _errorInfo: ErrorInfo) {
    // Error logged - fallback UI will be shown
  }

  render() {
    if (this.state.hasError) {
      return (
        <CallbackErrorFallback
          message={this.props.fallbackMessage}
          onRetry={() => this.setState({ hasError: false })}
        />
      )
    }

    return this.props.children
  }
}

const CallbackErrorFallback = ({
  message,
  onRetry
}: {
  message?: string
  onRetry: () => void
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <Flex
      orientation="vertical"
      gap={24}
      align="center"
      justify="center"
      className="p-8"
    >
      <Alert
        type="error"
        title={message || t('errors.somethingWentWrong')}
        showIcon
      />
      <Flex gap={12}>
        <Button onClick={onRetry}>{t('common.retry')}</Button>
        <Button type="primary" onClick={() => navigate(paths.auth.login)}>
          {t('common.backToLogin')}
        </Button>
      </Flex>
    </Flex>
  )
}
